import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowRight, Check } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function EmailSignupForm({
  source = "hero",
  testIdPrefix = "hero",
  dark = false,
  redirectTo = "/welcome",
  successDescription,
}) {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || loading) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/subscribe`, {
        email,
        first_name: firstName || null,
        source,
      });
      const data = res.data;
      if (data.status === "already_subscribed") {
        toast(data.message || "You're already on the list.", {
          description: successDescription || "Welcome back. Here's what else lives here.",
        });
      } else {
        toast(data.message || "You're in.", {
          description: successDescription || "Your guide is on its way. Welcome.",
        });
      }
      setSuccess(true);
      if (redirectTo) {
        setTimeout(() => {
          navigate(redirectTo, {
            state: {
              firstName: firstName || null,
              email: email || null,
            },
          });
        }, 600);
      }
    } catch (err) {
      const message =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof message === "string" ? message : "Couldn't subscribe.", {
        description: "Please check the email address and try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const labelColor = dark ? "text-[#F7F5F2]/60" : "text-[#5C5C5C]";
  const inputBase = dark
    ? "bg-transparent text-[#F7F5F2] placeholder:text-[#F7F5F2]/40 border-0 border-b border-[#F7F5F2]/30 rounded-none px-0 h-12 text-base focus-visible:ring-0 focus-visible:border-[#F7F5F2]"
    : "bg-transparent text-[#1A1A1A] placeholder:text-[#5C5C5C]/70 border-0 border-b border-[#1A1A1A]/40 rounded-none px-0 h-12 text-base focus-visible:ring-0 focus-visible:border-[#1FA5B8]";

  if (success) {
    return (
      <div
        data-testid={`${testIdPrefix}-signup-success`}
        className={`p-6 border ${dark ? "border-[#F7F5F2]/30 bg-[#F7F5F2]/5" : "border-[#E5E0D8] bg-[#FFFFFF]"}`}
      >
        <div className="flex items-center gap-3">
          <span
            className={`h-8 w-8 inline-flex items-center justify-center ${
              dark ? "bg-[#F7F5F2] text-[#1A1A1A]" : "bg-[#1FA5B8] text-[#F7F5F2]"
            }`}
          >
            <Check size={16} strokeWidth={2} />
          </span>
          <div>
            <div className={`font-display text-xl ${dark ? "text-[#F7F5F2]" : "text-[#1A1A1A]"}`}>
              You&apos;re <span className="italic font-light">in</span>.
            </div>
            <div className={`font-body text-sm mt-1 ${dark ? "text-[#F7F5F2]/70" : "text-[#5C5C5C]"}`}>
              Check your inbox. The welcome note is on its way.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form
      data-testid={`${testIdPrefix}-signup-form`}
      onSubmit={handleSubmit}
      className="w-full"
    >
      <div className="grid grid-cols-1 sm:grid-cols-5 gap-4 items-end">
        <div className="sm:col-span-2">
          <label
            htmlFor={`${testIdPrefix}-first-name`}
            className={`block text-[10px] uppercase tracking-[0.28em] font-body ${labelColor}`}
          >
            First name (optional)
          </label>
          <Input
            id={`${testIdPrefix}-first-name`}
            data-testid={`${testIdPrefix}-first-name-input`}
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="Mira"
            className={inputBase}
            autoComplete="given-name"
          />
        </div>
        <div className="sm:col-span-3">
          <label
            htmlFor={`${testIdPrefix}-email`}
            className={`block text-[10px] uppercase tracking-[0.28em] font-body ${labelColor}`}
          >
            Email
          </label>
          <Input
            id={`${testIdPrefix}-email`}
            data-testid={`${testIdPrefix}-email-input`}
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@studio.com"
            className={inputBase}
            autoComplete="email"
          />
        </div>
      </div>

      <Button
        type="submit"
        data-testid={`${testIdPrefix}-submit-button`}
        disabled={loading}
        className={`mt-6 rounded-none h-12 px-6 font-body text-[12px] uppercase tracking-[0.22em] inline-flex items-center gap-2 ${
          dark
            ? "bg-[#F7F5F2] hover:bg-white text-[#1A1A1A]"
            : "bg-[#1FA5B8] hover:bg-[#178391] text-[#F7F5F2]"
        }`}
      >
        {loading ? "Sending…" : "Open the doorway"}
        {!loading && <ArrowRight size={16} strokeWidth={1.6} />}
      </Button>
    </form>
  );
}
